import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/auth/AuthProvider";
import { useAlmacen } from "@/lib/almacen";
import { useRpc, CLAVES_DOMINIO } from "@/lib/useRpc";
import { mapearErrorSupabase } from "@/lib/errores";
import { fechaHora } from "@/lib/formato";
import { Tarjeta, Boton, Campo, Input, Select, Badge, Esqueleto } from "@/components/ui";
import { useToast } from "@/components/ui/Toast";

export function Ajustes() {
  const { perfil, esAdmin, recargarPerfil } = useAuth();
  const { almacenes } = useAlmacen();
  const qc = useQueryClient();
  const toast = useToast();
  const emp = perfil?.empresa;
  const [nombre, setNombre] = useState(emp?.nombre ?? "");
  const [modo, setModo] = useState(emp?.modo_ventas ?? "manual");
  const [diasAlerta, setDiasAlerta] = useState(String(emp?.dias_alerta ?? 15));
  const [codAlm, setCodAlm] = useState(""); const [nomAlm, setNomAlm] = useState("");
  const [guardando, setGuardando] = useState(false);
  const esDueno = perfil?.usuario.rol === "dueno";
  const esDemo = emp?.slug === "demo" || emp?.slug === "demo-erp";

  const usuarios = useQuery({ queryKey: ["empresa", "usuarios"], queryFn: async () => { const { data, error } = await supabase.from("usuarios_empresa").select("*").order("nombre"); if (error) throw error; return data; } });
  const pendientes = useQuery({ queryKey: ["pendientes"], queryFn: async () => { const { data, error } = await supabase.from("pendientes_catalogo").select("*").order("resuelto").order("fecha", { ascending: false }).limit(50); if (error) throw error; return data; } });
  const crearAlmacen = useRpc("crear_almacen", { onSuccess: () => { toast.exito("Almacén creado"); setCodAlm(""); setNomAlm(""); } });
  const reiniciar = useRpc("reiniciar_demo", { onSuccess: () => toast.exito("Demo reiniciada: datos como al principio.") });

  async function guardarEmpresa(e: FormEvent) {
    e.preventDefault();
    setGuardando(true);
    const d = Number(diasAlerta);
    const { error } = await supabase.from("empresas").update({ nombre: nombre.trim(), modo_ventas: modo, dias_alerta: Number.isFinite(d) && d >= 1 ? Math.min(365, Math.round(d)) : 15 }).eq("id", emp!.id);
    setGuardando(false);
    if (error) { toast.error(mapearErrorSupabase(error).mensaje); return; }
    await recargarPerfil();
    await Promise.all(CLAVES_DOMINIO.map((k) => qc.invalidateQueries({ queryKey: [k] })));
    toast.exito("Guardado");
  }

  async function resolver(id: string) {
    const { error } = await supabase.from("pendientes_catalogo").update({ resuelto: true }).eq("id", id);
    if (error) { toast.error(mapearErrorSupabase(error).mensaje); return; }
    qc.invalidateQueries({ queryKey: ["pendientes"] });
  }

  if (!esAdmin) return <div className="mx-auto max-w-2xl"><Tarjeta><p>Solo administradores.</p></Tarjeta></div>;
  if (!emp) return <Esqueleto />;

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <h1 className="text-2xl font-bold">Ajustes</h1>

      <Tarjeta titulo="Empresa">
        <form onSubmit={guardarEmpresa} className="space-y-3">
          <Campo etiqueta="Nombre" htmlFor="emp-nombre"><Input id="emp-nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} disabled={!esDueno} /></Campo>
          <Campo etiqueta="¿Tus ventas se registran en un sistema (ERP / punto de venta)?" htmlFor="emp-modo" ayuda={modo === "erp" ? "No se capturan ventas aquí; importas tu sistema y cuentas." : "Registras las ventas del día en bloque; EYE IA es tu inventario."}>
            <Select id="emp-modo" value={modo} onChange={(e) => setModo(e.target.value as "erp" | "manual")} disabled={!esDueno}><option value="manual">No, lo llevo en Excel o a mano</option><option value="erp">Sí, tengo ERP o punto de venta</option></Select>
          </Campo>
          <Campo etiqueta="Avisar cuando el stock alcance para menos de (días)" htmlFor="emp-dias" ayuda="Se usa con la rotación esperada de cada producto."><Input id="emp-dias" inputMode="numeric" value={diasAlerta} onChange={(e) => setDiasAlerta(e.target.value)} className="tabular w-32" disabled={!esDueno} /></Campo>
          {esDueno && <Boton type="submit" cargando={guardando}>Guardar</Boton>}
          {!esDueno && <p className="text-sm text-tinta-suave">Solo el dueño puede cambiar estos datos.</p>}
        </form>
      </Tarjeta>

      <Tarjeta titulo="Almacenes">
        <ul className="mb-3 divide-y divide-borde text-sm">{almacenes.map((a) => <li key={a.id} className="flex justify-between py-2"><span><span className="font-medium">{a.codigo}</span> · {a.nombre}</span></li>)}</ul>
        <form onSubmit={(e) => { e.preventDefault(); crearAlmacen.mutate({ p_codigo: codAlm, p_nombre: nomAlm }); }} className="flex flex-wrap items-end gap-2">
          <Campo etiqueta="Código" htmlFor="alm-cod"><Input id="alm-cod" value={codAlm} onChange={(e) => setCodAlm(e.target.value)} placeholder="TIENDA2" className="w-32" /></Campo>
          <Campo etiqueta="Nombre" htmlFor="alm-nom"><Input id="alm-nom" value={nomAlm} onChange={(e) => setNomAlm(e.target.value)} placeholder="Tienda Norte" /></Campo>
          <Boton type="submit" variante="secundario" disabled={!codAlm.trim() || !nomAlm.trim()} cargando={crearAlmacen.isPending}>Agregar almacén</Boton>
        </form>
      </Tarjeta>

      <Tarjeta titulo="Usuarios">
        {usuarios.isLoading ? <Esqueleto filas={2} /> : (
          <ul className="divide-y divide-borde text-sm">{usuarios.data?.map((u) => <li key={u.usuario_id} className="flex items-center justify-between py-2"><span>{u.nombre}</span><span className="flex gap-2"><Badge tono="neutro">{u.rol}</Badge>{!u.activo && <Badge tono="error">inactivo</Badge>}</span></li>)}</ul>
        )}
        <p className="mt-3 text-sm text-tinta-suave">Para agregar o quitar usuarios, escríbele a Fran o Zepe (soporte EYE IA). En la siguiente versión podrás invitar por correo.</p>
      </Tarjeta>

      <Tarjeta titulo={`Avisos del almacén (${pendientes.data?.filter((p) => !p.resuelto).length ?? 0} pendientes)`}>
        {pendientes.isLoading ? <Esqueleto filas={2} /> : (pendientes.data?.length ?? 0) === 0 ? <p className="text-sm text-tinta-suave">Cuando el almacenista reciba algo que no está en el catálogo, lo verás aquí.</p> : (
          <ul className="divide-y divide-borde text-sm">
            {pendientes.data!.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <span className={p.resuelto ? "text-tinta-suave line-through" : ""}><span className="font-medium">{p.sku_texto}</span>{p.nota ? ` · ${p.nota}` : ""} <span className="text-tinta-suave">· {fechaHora(p.fecha)}</span></span>
                {!p.resuelto && <Boton tamano="sm" variante="secundario" onClick={() => resolver(p.id)}>Marcar resuelto</Boton>}
              </li>
            ))}
          </ul>
        )}
      </Tarjeta>

      {esDemo && (
        <Tarjeta titulo="Demostración">
          <p className="mb-3 text-sm text-tinta-suave">Esta es una empresa de demostración. Reiniciar borra lo capturado en la demo y deja los datos como al principio.</p>
          <Boton variante="peligro" onClick={() => { if (window.confirm("¿Reiniciar la demo? Se borra todo lo capturado.")) reiniciar.mutate({} as never); }} cargando={reiniciar.isPending}>Reiniciar demo</Boton>
        </Tarjeta>
      )}
    </div>
  );
}
